<template>
  <div class="container mx-auto px-4 py-8">
    <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Vulnerability Reports</h1>
    <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">Triage incoming vulnerability disclosures from security researchers.</p>

    <!-- Filter -->
    <div class="flex flex-wrap items-center gap-2 mb-4">
      <button v-for="s in ['all','new','reviewing','accepted','rejected']" :key="s"
        @click="changeFilter(s)" class="px-3 py-1.5 text-xs font-medium rounded-full border transition-colors"
        :class="statusFilter === s
          ? 'bg-primary text-white border-primary'
          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'">
        {{ s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1) }}
      </button>
      <span v-if="totalCount > 0" class="text-xs text-gray-400 dark:text-gray-500 ml-auto">{{ totalCount }} total</span>
    </div>

    <div v-if="loading" class="text-center py-12 text-gray-500 dark:text-gray-400">Loading...</div>

    <div v-else-if="reports.length === 0" class="text-center py-12">
      <p class="text-gray-500 dark:text-gray-400">No vulnerability reports{{ statusFilter !== 'all' ? ` with status "${statusFilter}"` : '' }}.</p>
    </div>

    <div v-else class="space-y-3">
      <div v-for="report in reports" :key="report.ID" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div class="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors" @click="toggleExpand(report.ID)">
          <div class="flex items-start justify-between gap-2">
            <div class="flex-1 min-w-0">
              <div class="flex flex-wrap items-center gap-1.5 mb-1">
                <span class="px-2 py-0.5 text-xs font-medium rounded-full" :class="statusClass(report.status)">{{ report.status }}</span>
                <span v-if="report.severity" class="px-2 py-0.5 text-xs font-medium rounded-full" :class="sevClass(report.severity)">{{ report.severity }}</span>
              </div>
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white break-words">{{ report.title }}</h3>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-all">
                {{ report.reporter_name }} &lt;{{ report.reporter_email }}&gt;
                <span v-if="report.product_name"> · {{ report.product_name }}</span>
                · {{ formatDate(report.CreatedAt) }}
              </p>
            </div>
            <svg class="w-5 h-5 text-gray-400 flex-shrink-0 transition-transform mt-1" :class="expandedId === report.ID ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
          </div>
        </div>

        <div v-if="expandedId === report.ID" class="border-t border-gray-100 dark:border-gray-700 p-4 space-y-4">
          <div>
            <h4 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Description</h4>
            <p class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">{{ report.description }}</p>
          </div>
          <div v-if="report.steps_to_reproduce">
            <h4 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Steps to Reproduce</h4>
            <p class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">{{ report.steps_to_reproduce }}</p>
          </div>
          <div v-if="report.affected_version" class="text-sm text-gray-600 dark:text-gray-400">
            Affected version: <span class="font-medium">{{ report.affected_version }}</span>
          </div>
          <div v-if="report.review_note" class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm text-gray-600 dark:text-gray-400 break-words">
            <span class="font-medium">Review note:</span> {{ report.review_note }}
          </div>

          <!-- Actions -->
          <div v-if="report.status !== 'accepted'" class="pt-2 border-t border-gray-100 dark:border-gray-700 space-y-3">
            <input v-model="reviewNote" type="text" placeholder="Optional review note..."
              class="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700/50 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20" />
            <div class="flex flex-wrap gap-2">
              <button v-if="report.status === 'new'" @click="updateStatus(report.ID, 'reviewing')" :disabled="actionLoading"
                class="flex-1 min-w-[120px] px-3 py-2 text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors disabled:opacity-50 text-center">
                Mark Reviewing
              </button>
              <button @click="acceptReport(report.ID)" :disabled="actionLoading"
                class="flex-1 min-w-[120px] px-3 py-2 text-xs font-medium bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors disabled:opacity-50 text-center">
                Accept &amp; Create Vuln
              </button>
              <button @click="updateStatus(report.ID, 'rejected')" :disabled="actionLoading"
                class="flex-1 min-w-[120px] px-3 py-2 text-xs font-medium bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors disabled:opacity-50 text-center">
                Reject
              </button>
            </div>
          </div>
          <div v-else class="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
            <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
            Converted to vulnerability #{{ report.vulnerability_id }}
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="flex items-center justify-between mt-4">
      <button @click="goToPage(currentPage - 1)" :disabled="currentPage <= 1"
        class="px-3 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors">Previous</button>
      <span class="text-xs text-gray-500 dark:text-gray-400">Page {{ currentPage }} of {{ totalPages }}</span>
      <button @click="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages"
        class="px-3 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors">Next</button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import axios from 'axios'

const getAPIURL = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  const cleanUrl = envUrl.replace(/\/$/, '')
  return cleanUrl.endsWith('/api/v1') ? cleanUrl : `${cleanUrl}/api/v1`
}

export default {
  name: 'VulnReportsView',
  setup() {
    const store = useStore()
    const route = useRoute()
    const tenantId = computed(() => route.params.tenantId || store.getters.currentTenantId)
    const headers = computed(() => ({ Authorization: `Bearer ${store.state.token}` }))

    const reports = ref([])
    const loading = ref(true)
    const statusFilter = ref('all')
    const expandedId = ref(null)
    const reviewNote = ref('')
    const actionLoading = ref(false)
    const currentPage = ref(1)
    const totalPages = ref(1)
    const totalCount = ref(0)

    const fetchReports = async () => {
      if (!tenantId.value) return
      loading.value = true
      try {
        const params = { per_page: 25, page: currentPage.value, order_by: 'created_at', sort_direction: 'desc' }
        if (statusFilter.value !== 'all') params.status = statusFilter.value
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId.value}/vuln-reports`, { headers: headers.value, params })
        reports.value = res.data?.data || []
        totalCount.value = res.data?.totalEntries || 0
        totalPages.value = res.data?.totalPages || 1
        currentPage.value = res.data?.currentPage || currentPage.value
      } catch (e) {
        console.error('Failed to fetch vuln reports', e)
        reports.value = []
      } finally {
        loading.value = false
      }
    }

    const changeFilter = (s) => {
      statusFilter.value = s
      currentPage.value = 1
      fetchReports()
    }

    const goToPage = (page) => {
      if (page < 1 || page > totalPages.value) return
      currentPage.value = page
      fetchReports()
    }

    const toggleExpand = (id) => {
      expandedId.value = expandedId.value === id ? null : id
      reviewNote.value = ''
    }

    const updateStatus = async (id, status) => {
      actionLoading.value = true
      try {
        await axios.put(`${getAPIURL()}/admin/tenants/${tenantId.value}/vuln-reports/${id}`, {
          status, review_note: reviewNote.value
        }, { headers: headers.value })
        await fetchReports()
      } catch (e) {
        alert(e.response?.data?.error || 'Failed to update report')
      } finally {
        actionLoading.value = false
      }
    }

    const acceptReport = async (id) => {
      actionLoading.value = true
      try {
        await axios.post(`${getAPIURL()}/admin/tenants/${tenantId.value}/vuln-reports/${id}/accept`, {}, { headers: headers.value })
        await fetchReports()
      } catch (e) {
        alert(e.response?.data?.error || 'Failed to accept report')
      } finally {
        actionLoading.value = false
      }
    }

    const statusClass = (s) => ({
      'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300': s === 'new',
      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300': s === 'reviewing',
      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300': s === 'accepted',
      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300': s === 'rejected',
    })

    const sevClass = (sev) => ({
      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300': sev === 'CRITICAL',
      'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300': sev === 'HIGH',
      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300': sev === 'MEDIUM',
      'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300': sev === 'LOW',
    })

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''

    onMounted(fetchReports)

    return {
      reports, loading, statusFilter, expandedId, reviewNote, actionLoading,
      currentPage, totalPages, totalCount,
      changeFilter, goToPage, toggleExpand, updateStatus, acceptReport,
      statusClass, sevClass, formatDate
    }
  }
}
</script>
